/**
 * Offline Queue - IndexedDB + Background Sync
 * 
 * Cola de escrituras para sincronización offline.
 * Usa IndexedDB para persistencia y Background Sync API para reintento automático.
 */

interface QueuedWrite {
  id: string
  url: string
  method: string
  headers: Record<string, string>
  body: unknown
  timestamp: number
  retryCount: number
  idempotencyKey?: string
}

const DB_NAME = 'crm-vro-offline'
const DB_VERSION = 1
const STORE_NAME = 'queued-writes'

class OfflineQueueDB {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  async init(): Promise<void> {
    if (this.db) return
    if (this.initPromise) return this.initPromise

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
          store.createIndex('timestamp', 'timestamp', { unique: false })
          store.createIndex('idempotencyKey', 'idempotencyKey', { unique: false })
        }
      }
    })

    return this.initPromise
  }

  async add(write: Omit<QueuedWrite, 'id' | 'timestamp' | 'retryCount'>): Promise<string> {
    await this.init()
    const id = crypto.randomUUID()
    const now = Date.now()

    const queuedWrite: QueuedWrite = {
      ...write,
      id,
      timestamp: now,
      retryCount: 0,
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.add(queuedWrite)

      request.onsuccess = () => resolve(id)
      request.onerror = () => reject(request.error)
    })
  }

  async getAll(): Promise<QueuedWrite[]> {
    await this.init()
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async getByIdempotencyKey(key: string): Promise<QueuedWrite | undefined> {
    await this.init()
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('idempotencyKey')
      const request = index.get(key)

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async update(write: QueuedWrite): Promise<void> {
    await this.init()
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(write)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async delete(id: string): Promise<void> {
    await this.init()
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clear(): Promise<void> {
    await this.init()
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }
}

export const offlineQueueDB = new OfflineQueueDB()

/**
 * Servicio de cola offline con Background Sync
 */
class OfflineQueueService {
  private isProcessing = false
  private syncRegistration: ServiceWorkerRegistration | null = null
  private listeners: Set<(pending: number) => void> = new Set()

  async init(): Promise<void> {
    await offlineQueueDB.init()
    
    if ('serviceWorker' in navigator) {
      this.syncRegistration = await navigator.serviceWorker.ready
      
      // Escuchar mensajes del SW personalizado para forzar sincronización
      navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data?.type === 'force-sync') {
          this.processQueue()
        }
      })
    }

    // Escuchar eventos online/offline
    window.addEventListener('online', () => this.processQueue())
    window.addEventListener('offline', () => this.notifyListeners())

    // Procesar cola al iniciar si hay conexión
    if (navigator.onLine) {
      this.processQueue()
    }
  }

  /**
   * Añadir una escritura a la cola offline
   */
  async enqueue(
    url: string,
    method: string,
    body: unknown,
    headers: Record<string, string> = {},
    idempotencyKey?: string
  ): Promise<string> {
    // Si hay idempotencyKey, verificar si ya existe
    if (idempotencyKey) {
      const existing = await offlineQueueDB.getByIdempotencyKey(idempotencyKey)
      if (existing) {
        return existing.id // Ya encolado, evitar duplicados
      }
    }

    const id = await offlineQueueDB.add({
      url,
      method: method.toUpperCase(),
      headers: { 'Content-Type': 'application/json', ...headers },
      body,
      idempotencyKey,
    })

    this.notifyListeners()

    // Si hay conexión, intentar procesar inmediatamente
    if (navigator.onLine) {
      this.processQueue()
    } else {
      // Registrar Background Sync para cuando vuelva la conexión
      await this.registerBackgroundSync()
    }

    return id
  }

  /**
   * Procesar toda la cola pendiente
   */
  async processQueue(): Promise<void> {
    if (this.isProcessing || !navigator.onLine) return

    this.isProcessing = true

    try {
      const queue = await offlineQueueDB.getAll()
      
      for (const write of queue) {
        if (!navigator.onLine) break // Se fue la conexión

        try {
          const response = await fetch(write.url, {
            method: write.method,
            headers: write.headers,
            body: write.body ? JSON.stringify(write.body) : undefined,
          })

          if (response.ok || response.status === 409 || response.status === 422) {
            // Éxito o error de validación/conflicto (no reintentar)
            await offlineQueueDB.delete(write.id)
          } else if (response.status >= 500) {
            // Error de servidor, reintentar
            await this.incrementRetry(write)
            await this.registerBackgroundSync()
          } else {
            // Error 4xx no recuperable (401, 403, etc.), eliminar de cola
            await offlineQueueDB.delete(write.id)
          }
        } catch {
          // Error de red, reintentar
          await this.incrementRetry(write)
          await this.registerBackgroundSync()
        }
      }
    } finally {
      this.isProcessing = false
      this.notifyListeners()
    }
  }

  private async incrementRetry(write: QueuedWrite): Promise<void> {
    write.retryCount++
    if (write.retryCount >= 5) {
      // Demasiados reintentos, eliminar y notificar
      await offlineQueueDB.delete(write.id)
      console.warn('Offline write failed after 5 retries:', write)
    } else {
      await offlineQueueDB.update(write)
    }
  }

  private async registerBackgroundSync(): Promise<void> {
    if (!this.syncRegistration || !('sync' in window.ServiceWorkerRegistration.prototype)) {
      return
    }

    try {
      await (this.syncRegistration as any).sync.register('crm-vro-offline-sync')
    } catch (error) {
      console.warn('Background Sync registration failed:', error)
    }
  }

  /**
   * Obtener número de escrituras pendientes
   */
  async getPendingCount(): Promise<number> {
    const queue = await offlineQueueDB.getAll()
    return queue.length
  }

  /**
   * Suscribirse a cambios en la cola
   */
  subscribe(listener: (pending: number) => void): () => void {
    this.listeners.add(listener)
    this.getPendingCount().then(listener)
    return () => this.listeners.delete(listener)
  }

  private async notifyListeners(): Promise<void> {
    const count = await this.getPendingCount()
    this.listeners.forEach(l => l(count))
  }

  /**
   * Forzar sincronización manual (para botón "Sincronizar ahora")
   */
  async forceSync(): Promise<void> {
    await this.processQueue()
  }
}

export const offlineQueue = new OfflineQueueService()

/**
 * Helper para encolar escrituras desde la API
 */
export async function enqueueWrite(
  url: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body: unknown,
  headers?: Record<string, string>,
  idempotencyKey?: string
): Promise<string> {
  return offlineQueue.enqueue(url, method, body, headers, idempotencyKey)
}